import * as React from "react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {requestApi} from "@/boxhub-api";
import OrderBanner from "@/common/order/OrderBanner";
import {QuoteStatus, RecipientSelectionDTO, RequestActions, RequestDTO, RequestStatus} from "@/api/data-contracts";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {AppPaths} from "@/app/AppPaths";
import {AppFrame} from "@/app/AppFrame";
import {useModelState} from "@/lib/modelstate";
import {useRequestSchema} from "@/pages/internal-request-page/schema";
import {requestReducer} from "@/model/request";
import {Grid2 as Grid, useTheme} from "@mui/material";
import ConfirmationDialog from "@/lib/components/ConfirmationDialog";
import {AxiosResponse} from "axios";
import {useSnackbarEx} from "@/lib/snackbarex";
import {useDialogController} from "@/lib/dialog-controller";
import {RequestCard} from "@/common/request-cards/RequestCard";
import {OrderDocumentsCard} from "@/common/request-cards/OrderDocumentsCard";
import {ProductCard} from "@/common/request-cards/ProductCard";
import {PricingCard} from "@/common/request-cards/PricingCard";
import {PalletizingCard} from "@/common/request-cards/PalletizingCard";
import {IconButtonEx} from "@/lib/components/buttons/IconButtonEx";
import {useTranslation} from "react-i18next";
import {CommonI18N, NotificationI18N, RequestI18N} from "@/translations";
import {QuoteCard} from "@/common/request-cards/QuoteCard";
import {CancellationReasonCard} from "@/common/request-cards/CancellationReasonCard";
import {RecipientScopeCard} from "@/common/request-cards/RecipientScopeCard";
import {FeedbackReasonCard} from "@/common/request-cards/FeedbackReasonCard";
import {InternalConfirmationCard} from "@/common/request-cards/InternalConfirmationCard";
import {withAuthWrapperComponent} from "@/app/auth/WithAuthWrapperComponent";
import {ShortCutHandler} from "@/lib/shortcut-handler";
import {useRequestDatesValidator} from "@/pages/internal-request-page/useRequestDatesValidator";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import {BriefIcon, KopierenIcon} from "@/agentur/AgenturIcons";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {isOperatorRole, useAuth} from "@/app/auth/AuthContext";
import NotificationsWithPanel from "@/common/notifications/NotificationsWithPanel";
import {ValidationError} from "yup";
import {useRequestDialogs} from "@/common/request-details/useRequestDialogs";
import RequestBanner from "@/common/request-details/RequestBanner";
import RecipientSelectionDialog from "@/common/request-details/RecipientSelectionDialog";

const DATE_FIELD_PATHS = new Set(["validUntil", "orderData.deliveryDateFrom", "orderData.deliveryDateTo"]);

const InternalRequestPage = () => {
    const { t } = useTranslation();
    const { roles } = useAuth();
    const isOperator = isOperatorRole(roles);
    const { id: requestId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const [processing, setProcessing] = useState(false);
    const [skipUnsavedCheck, setSkipUnsavedCheck] = useState(false);

    const { showMessage } = useSnackbarEx();
    const [DialogNode, showDialog] = useDialogController();

    const schema = useRequestSchema();

    const [companyIdx, setCompanyIdx] = useState<number>(0);
    const [versionIdx, setVersionIdx] = useState<number>(0);

    const navigateToOverview = useCallback(
        (forceLeave: boolean) => {
            const overviewPath = isOperator ? AppPaths.OPERATOR_REQUEST_OVERVIEW : AppPaths.INTERNAL_REQUEST_OVERVIEW;
            if (forceLeave) {
                setSkipUnsavedCheck(true);
                setTimeout(() => navigate(overviewPath, { replace: true }), 20);
            } else {
                navigate(overviewPath, { replace: true });
            }
        },
        [navigate, isOperator]
    );

    const fetchRequest = async (_id: number | string) => {
        if (requestId === "create") {
            return requestApi.getRequestTemplate();
        }
        if (requestId === "clone") {
            const sourceId = searchParams.get("sourceId");
            if (sourceId == null) {
                throw new Error("No sourceId provided");
            }
            return requestApi.getRequestCloned(+sourceId);
        }
        if (requestId != null) {
            return requestApi.getRequest(+requestId);
        }
        throw new Error("No requestId provided");
    };

    const handleCopy = () => {
        const boxhubNumber = getModel().boxhubNumber;
        if (boxhubNumber) {
            if (navigator.clipboard) {
                navigator.clipboard.writeText(boxhubNumber).then(
                    () => {
                        showMessage({ summary: t(RequestI18N.details.message.clipboardCopied), severity: "success" });
                    },
                    (err: unknown) => {
                        showMessage({
                            summary: t(RequestI18N.details.message.clipboardFailed),
                            severity: "error",
                            error: err
                        });
                    }
                );
            } else {
                showMessage({ summary: t(RequestI18N.details.message.clipboardNotSupported), severity: "error" });
            }
        }
    };

    const showPublishSuccessDialog = () => {
        showDialog((open, close) => (
            <ConfirmationDialog
                open={open}
                onClose={close}
                closeOnConfirm
                onConfirm={() => navigateToOverview(true)}
                message={
                    <>
                        {t(RequestI18N.details.message.model)}:
                        <span style={{ fontWeight: "bold", marginLeft: 8 }}>{getModel().boxhubNumber}</span>
                        <IconButtonEx
                            icon={<KopierenIcon mode={theme.palette.mode} />}
                            onClick={handleCopy}
                            title={t(RequestI18N.details.action.copy)}
                        />
                    </>
                }
                title={t(RequestI18N.details.message.publishSuccessTitle)}
                confirmButtonText={t(RequestI18N.details.message.publishSuccessConfirm)}
                maxWidth="sm"
                fullWidth
                dataCy="request-publish-success-dialog"
            />
        ));
    };

    /**
     * Runs the mandatory-field check (model validation plus the schema, ignoring date-field errors the
     * separate date validator already owns) and returns whether the model may be saved. Extracted so the
     * publish flow can run it BEFORE opening the recipient-selection dialog: a request with blocking
     * errors must show its field errors and never reach the dialog.
     */
    const checkMandatoryFields = (): boolean => {
        validate();

        let hasBlockingErrors = false;
        try {
            schema.validateSync(getModel(), { abortEarly: false, disableStackTrace: true });
        } catch (error) {
            if (error instanceof ValidationError) {
                hasBlockingErrors = error.inner.some((err) => !DATE_FIELD_PATHS.has(err.path ?? ""));
            }
        }

        return !hasBlockingErrors;
    };

    const doSave = async (publish: boolean, recipientSelection?: RecipientSelectionDTO) => {
        if (!checkMandatoryFields()) {
            throw new Error("Validation failed");
        }

        setProcessing(true);
        let promise: Promise<AxiosResponse<RequestDTO>>;
        if (publish) {
            const payload: RequestDTO = { ...model, recipientSelection };
            promise = model?.id
                ? requestApi.updateAndPublishRequest(model.id, payload)
                : requestApi.createAndPublishRequest(payload);
        } else {
            promise = model?.id ? requestApi.updateRequest(model.id, model) : requestApi.createRequest(model);
        }

        try {
            const requestResult = await promise;
            const newRequest = requestResult.data;
            dispatch({ type: "--set", data: newRequest, nochange: true });
            showMessage({
                summary: t(RequestI18N.details.message.requestSavedSuccess, {
                    boxhubNumber: newRequest.boxhubNumber
                }),
                severity: "success"
            });
            setTimeout(() => {
                if (requestId === "create" || requestId === "clone") {
                    navigate(AppPaths.INTERNAL_REQUEST_fn(newRequest.id!), { replace: true });
                }
                if (publish) {
                    showPublishSuccessDialog();
                }
            }, 250);
        } catch (error: unknown) {
            setErrors(error as Parameters<typeof setErrors>[0], false);
        } finally {
            setProcessing(false);
        }
    };

    /**
     * "Veröffentlichen" no longer publishes directly: it first runs the mandatory-field check (blocking
     * errors stay exactly as before, no dialog opens), then opens the "Empfänger der Anfrage festlegen"
     * dialog. Only that dialog's own "Veröffentlichen" confirms the actual `doSave(true, selection)`
     * call, which attaches the collected recipient selection to the publish payload; the server resolves
     * and validates it before the request is actually published (Task #1752).
     */
    const handlePublishClick = () => {
        if (!checkMandatoryFields()) {
            return;
        }
        showDialog((open, close) => (
            <RecipientSelectionDialog
                open={open}
                onClose={close}
                onConfirm={(selection) => doSave(true, selection)}
                deliveryAddressId={model.deliveryAddress?.id}
            />
        ));
    };

    const validateDates = useRequestDatesValidator(false);

    const validator = async (model: RequestDTO) => {
        if (model.orderData == null || readonly) {
            return {};
        }
        return validateDates({
            deliveryDateFrom: model.orderData.deliveryDateFrom,
            deliveryDateTo: model.orderData.deliveryDateTo,
            validUntil: model.validUntil
        });
    };

    const { model, errors, onChange, status, isDirty, dispatch, setErrors, getModel, validate, httpErrorStatus } =
        useModelState<RequestDTO, number | "template">({
            initialState: {} as any,
            reducer: requestReducer,
            schema,
            validator,
            id: requestId == null ? null : +requestId,
            loader: fetchRequest
        });

    const quotesByCompany = useMemo(() => {
        const filtered = model.quotes?.filter((q) => q.status !== QuoteStatus.REJECTED) ?? [];
        const map = new Map<number, typeof filtered>();
        for (const q of filtered) {
            const cid = q.company?.id ?? 0;
            if (!map.has(cid)) {
                map.set(cid, []);
            }
            map.get(cid)!.push(q);
        }
        for (const versions of map.values()) {
            versions.sort((a, b) => (a.quoteVersion ?? 1) - (b.quoteVersion ?? 1));
        }
        return [...map.values()];
    }, [model.quotes]);

    const safeCompanyIdx = Math.min(companyIdx, Math.max(0, quotesByCompany.length - 1));
    const companyQuotes = quotesByCompany[safeCompanyIdx] ?? [];

    useEffect(() => {
        if (companyQuotes.length > 0) {
            setVersionIdx(companyQuotes.length - 1);
        }
    }, [model?.quotes]);

    const quote = companyQuotes[versionIdx] ?? companyQuotes.at(-1) ?? null;
    const quoteIdx = useMemo(() => model.quotes?.findIndex((q) => q.id === quote?.id) ?? -1, [model.quotes, quote?.id]);
    const orderData = quote ? quote.orderData : model.orderData;
    const orderDataOrg = quote ? model.orderData : null;
    const readonly = isOperator || (model?.status !== RequestStatus.CREATED && model?.status !== RequestStatus.DRAFT);
    const canGiveFeedback =
        !isOperator &&
        model?.allowedActions?.includes(RequestActions.GIVE_FEEDBACK) &&
        quote?.status === QuoteStatus.SUBMITTED;
    const canExtendDates = !isOperator && !!model?.allowedActions?.includes(RequestActions.EXTEND_DATES);

    const dialogHandlers = useRequestDialogs({
        model,
        dispatch,
        showDialog,
        showMessage,
        quote,
        quotesByCompany,
        setCompanyIdx,
        setVersionIdx,
        navigateToOverview,
        setProcessing,
        isOperator
    });

    useEffect(() => {
        if (model?.status === RequestStatus.CONFIRMED) {
            const cIdx = quotesByCompany.findIndex((versions) =>
                versions.some((q) => q.status === QuoteStatus.ACCEPTED)
            );
            if (cIdx >= 0) {
                setCompanyIdx(cIdx);
                const vIdx = quotesByCompany[cIdx].findIndex((q) => q.status === QuoteStatus.ACCEPTED);
                setVersionIdx(Math.max(vIdx, 0));
            }
        }
    }, [model?.quotes, model?.status]);

    const searchParamsRef = useRef(searchParams);
    const setSearchParamsRef = useRef(setSearchParams);
    useEffect(() => {
        if (searchParamsRef.current.has("tab")) {
            const next = new URLSearchParams(searchParamsRef.current);
            next.delete("tab");
            setSearchParamsRef.current(next, { replace: true });
        }
    }, []);

    const buttons = (
        <Grid container direction="row" spacing={2}>
            <ButtonEx
                startIcon={<ArrowBackIosIcon />}
                onClick={() => navigateToOverview(false)}
                label={t(RequestI18N.details.action.backToOverview)}
            />
            <ButtonEx
                visible={!!model?.id}
                startIcon={<BriefIcon mode={theme.palette.mode} />}
                onClick={dialogHandlers.handleOpenHistoryDialog}
                label={t(NotificationI18N.emailHistory.action.open)}
            />
            <ButtonEx
                visible={!isOperator && !!model?.id}
                data-cy="copy-request-button"
                label={t(RequestI18N.details.action.copyRequest)}
                color="primary"
                startIcon={<KopierenIcon mode={theme.palette.mode} />}
                to={AppPaths.INTERNAL_REQUEST_fn("clone", model.id)}
                disabled={isDirty}
            />
        </Grid>
    );

    const shortcuts = useMemo(
        () => ({
            "ALT-ArrowLeft": () => {
                setCompanyIdx((idx) => Math.max(0, idx - 1));
                setVersionIdx(0);
            },
            "ALT-ArrowRight": () => {
                setCompanyIdx((idx) => Math.min(quotesByCompany.length - 1, idx + 1));
                setVersionIdx(0);
            }
        }),
        [quotesByCompany.length]
    );

    const refreshOrder = useCallback(() => {
        if (model.id) {
            requestApi
                .getRequest(model.id)
                .then((r) => dispatch({ type: "--set", data: r.data, nochange: true }))
                .catch(() => showMessage({ summary: t(CommonI18N.message.unknownError), severity: "error" }));
        }
    }, [model.id, dispatch, showMessage, t]);

    const cardGrid = (forceReadonly: boolean) => {
        const isReadonly = forceReadonly || readonly;
        return (
            <Grid container spacing={2} paddingTop={1} size={{ xs: 12 }}>
                <Grid container direction="column" spacing={2} size={{ sm: 12, md: 4 }} alignContent="flex-start">
                    <Grid size={{ xs: 12 }}>
                        <RequestCard
                            model={model}
                            errors={isReadonly ? {} : errors}
                            onChange={onChange}
                            readonly={isReadonly}
                            mode="request"
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <OrderDocumentsCard
                            orderDocuments={model?.orderDocuments}
                            dispatch={dispatch}
                            readonly={isReadonly}
                            additionalInformation={model.additionalInformation}
                            additionalInformationError={isReadonly ? undefined : errors?.additionalInformation}
                            onChange={onChange}
                            mode={model.quotes?.length ? "accept" : "request"}
                            allowedActions={model.allowedActions}
                            onEditAdditionalInformation={dialogHandlers.handleEditAdditionalInformation}
                            showAdditionalInformation
                            emphasized
                        />
                    </Grid>
                </Grid>
                <Grid container direction="column" spacing={2} size={{ sm: 12, md: 4 }} alignContent="flex-start">
                    <Grid size={{ xs: 12 }}>
                        <ProductCard
                            orderData={orderData}
                            orgData={orderDataOrg}
                            errors={isReadonly ? {} : errors}
                            onChange={onChange}
                            readonly={isReadonly}
                            mode={model.quotes?.length ? "accept" : "request"}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <PalletizingCard
                            orderData={orderData}
                            orgData={orderDataOrg}
                            errors={isReadonly ? {} : errors}
                            onChange={onChange}
                            readonly={isReadonly}
                            mode={model.quotes?.length ? "accept" : "request"}
                        />
                    </Grid>
                </Grid>
                <Grid container direction="column" spacing={2} size={{ sm: 12, md: 4 }} alignContent="flex-start">
                    <Grid size={{ xs: 12 }}>
                        <PricingCard
                            orderData={orderData}
                            orgData={orderDataOrg}
                            errors={isReadonly ? {} : errors}
                            quoteStatus={quote?.status}
                            onChange={onChange}
                            dispatch={dispatch}
                            readonly={isReadonly}
                            mode={model.quotes?.length ? "accept" : "request"}
                            quoteIdx={quoteIdx}
                        />
                    </Grid>
                    {isOperator && model.cancellationReason && (
                        <Grid size={{ xs: 12 }}>
                            <CancellationReasonCard cancellationReason={model.cancellationReason} />
                        </Grid>
                    )}
                    {model.publishedAt && (
                        <Grid size={{ xs: 12 }}>
                            <RecipientScopeCard recipientScope={model.recipientScope} />
                        </Grid>
                    )}
                    {quote && (
                        <Grid size={{ xs: 12 }}>
                            <QuoteCard
                                model={quote}
                                errors={errors}
                                requestStatus={model.status}
                                onChange={onChange}
                                readonly={isReadonly}
                                mode={model.quotes?.length ? "accept" : "request"}
                                quotes={quotesByCompany.map((versions) => versions.at(-1)!)}
                                quoteIdx={safeCompanyIdx}
                                setQuoteIdx={(idx) => {
                                    setCompanyIdx(idx);
                                    setVersionIdx(0);
                                }}
                                companyVersions={companyQuotes}
                                versionIdx={versionIdx}
                                setVersionIdx={setVersionIdx}
                                handleConfirmQuote={dialogHandlers.handleConfirmQuote}
                                handleDismissQuote={dialogHandlers.handleDismissQuote}
                                handleGiveFeedback={dialogHandlers.handleGiveFeedback}
                                canGiveFeedback={canGiveFeedback}
                                showRejectReasonUnder="status"
                            />
                        </Grid>
                    )}
                    {isOperator && model.feedbackReason && (
                        <Grid size={{ xs: 12 }}>
                            <FeedbackReasonCard
                                feedbackReason={model.feedbackReason}
                                feedbackFreeText={model.feedbackFreeText}
                            />
                        </Grid>
                    )}
                    {isOperator && model.internalConfirmationReason && (
                        <Grid size={{ xs: 12 }}>
                            <InternalConfirmationCard
                                internalConfirmationReason={model.internalConfirmationReason}
                                internalConfirmationDate={model.internalConfirmationDate}
                                internalConfirmationBy={model.internalConfirmationBy}
                            />
                        </Grid>
                    )}
                </Grid>
            </Grid>
        );
    };

    return (
        <ShortCutHandler shortcuts={shortcuts}>
            <AppFrame
                loading={status === "loading"}
                modelState={status}
                httpErrorStatus={httpErrorStatus}
                buttons={buttons}
                isDirty={!skipUnsavedCheck && isDirty && !readonly}
            >
                {model?.order ? (
                    <OrderBanner
                        order={model.order}
                        onStatusAdvanced={refreshOrder}
                        onCancelled={refreshOrder}
                        onDeliveryDateUpdated={refreshOrder}
                    />
                ) : (
                    <RequestBanner
                        model={model}
                        isOperator={isOperator}
                        readonly={readonly}
                        processing={processing}
                        canExtendDates={canExtendDates}
                        hasQuotes={!!model?.quotes && model.quotes.length >= 1}
                        handlers={dialogHandlers}
                        onSave={() => doSave(false)}
                        onPublish={handlePublishClick}
                    />
                )}
                {cardGrid(false)}
                <DialogNode />
                {model?.id && <NotificationsWithPanel requestId={model.id} />}
            </AppFrame>
        </ShortCutHandler>
    );
};
export default withAuthWrapperComponent(InternalRequestPage);
